import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { getDetailById, getRelatedItems } from "@/lib/film";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const { id = "" } = useParams();
  const detail = useMemo(() => getDetailById(id), [id]);
  const { isFavorite, toggle } = useFavorites();

  useSeo({
    title: detail ? `${detail.title} - 蜜桃成熟时3D` : "内容未找到 - 蜜桃成熟时3D",
    description: detail
      ? `${detail.title}｜${detail.subtitle}。蜜桃成熟时3D影视资讯门户提供剧情、演员与影片相关内容的详细介绍。`
      : "未找到对应的内容，请返回首页浏览更多蜜桃成熟时3D影视资讯。",
    keywords: detail ? `${detail.title},蜜桃成熟时3D,${detail.categoryLabel}` : "蜜桃成熟时3D",
    path: `/detail/${id}`,
  });

  if (!detail) {
    return (
      <div className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="没有找到这条内容"
            description="链接可能已失效，或内容已被移除。你可以返回首页，或前往对应分类页继续浏览。"
            actionLabel="返回首页"
            actionTo="/"
          />
        </div>
      </div>
    );
  }

  const paragraphs = detail.body.split("\n\n").filter(Boolean);
  const related = getRelatedItems(detail, 4);
  const favorited = isFavorite(detail.id);

  return (
    <>
      <section className="relative">
        <div className="relative h-64 w-full md:h-96">
          <img
            src={detail.image}
            alt={detail.title}
            title={`${detail.title} 蜜桃成熟时3D`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10"></div>
          <div className="absolute inset-x-0 bottom-0">
            <div className="mx-auto max-w-[1200px] px-4 pb-6 md:px-6 md:pb-8">
              <span className="mb-3 inline-block rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                {detail.categoryLabel}
              </span>
              <h1 className="mb-2 max-w-4xl font-heading text-2xl font-bold text-background-50 md:text-4xl">
                {detail.title}
              </h1>
              <p className="text-sm text-background-100/90 md:text-base">{detail.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-8 md:py-12">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <Breadcrumb items={[{ label: detail.categoryLabel, path: detail.categoryPath }, { label: detail.title }]} />

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
            <article className="lg:col-span-2">
              <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-background-200 pb-5">
                {detail.meta.map((meta) => (
                  <div key={meta.label} className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-400">{meta.label}</span>
                    <span className="font-medium text-foreground-800">{meta.value}</span>
                  </div>
                ))}
              </div>

              <div>
                {paragraphs.map((para, index) => (
                  <p key={index} className="mb-5 text-[15px] leading-8 text-foreground-700">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {detail.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
                <button
                  type="button"
                  onClick={() => toggle(detail.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "border border-accent-300 text-accent-700 hover:bg-accent-50"
                  }`}
                >
                  <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} w-4 h-4 flex items-center justify-center`}></i>
                  {favorited ? "已收藏" : "收藏此内容"}
                </button>
                <span className="text-xs text-foreground-400">
                  {favorited ? "已加入本地收藏（仅保存在当前浏览器，用于演示）" : "收藏状态仅保存在本地浏览器，用于演示"}
                </span>
              </div>
            </article>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-links-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
                  分类入口
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "新闻资讯", path: "/news", icon: "ri-newspaper-line" },
                    { label: "剧情介绍", path: "/plot", icon: "ri-book-2-line" },
                    { label: "演员阵容", path: "/cast", icon: "ri-user-star-line" },
                    { label: "影视推荐", path: "/recommend", icon: "ri-movie-2-line" },
                  ].map((entry) => (
                    <Link
                      key={entry.path}
                      to={entry.path}
                      className="flex items-center gap-2 rounded-md bg-background-50 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className={`${entry.icon} w-4 h-4 flex items-center justify-center text-primary-600`}></i>
                      {entry.label}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-primary-100 bg-primary-50 p-5">
                <h2 className="mb-2 font-heading text-base font-bold text-foreground-950">关于本片</h2>
                <p className="text-sm leading-relaxed text-foreground-600">
                  「蜜桃成熟时3D」是一部以蜜桃园为背景、讲述成长与重逢故事的影片。想了解更多内容，可以前往剧情介绍或演员阵容页面继续阅读。
                </p>
                <Link
                  to="/plot"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700"
                >
                  查看完整剧情
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <div className="mt-12 border-t border-background-200 pt-10">
              <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关内容推荐</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((item) => (
                  <Link
                    key={`${item.type}-${item.id}`}
                    to={item.path}
                    className="group flex gap-3 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="mb-1 inline-block w-fit rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {item.typeLabel}
                      </span>
                      <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}